import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { Router } from '@angular/router';
import { LoginInfoService } from '@pg-app/auth/services/login-info.service';
import { CommonDialogComponent } from '@pg-app/common/common-dialog/common-dialog.component';
import { PortalType } from '@pg-app/core/Enum/Authentication/PortalType';
import { AuthService } from '@pg-app/core/services/auth.service';
import { BrandPrefixEnum } from '@pg-env/brand-prefix.enum';
import { environment } from '@pg-env/environment';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'np-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  showError = false;
  isClient = true;
  portalType = PortalType.ClientPortal;
  errorMessage: string;
  showAlertChrome = false;
  isPrivalgoMarkets = environment.isPrivalgoMarkets;
  brandName = environment.brandName;
  domain = environment.domain;

  constructor(
    private auth: AuthService,
    private fb: FormBuilder,
    private router: Router,
    private loginInfo: LoginInfoService,
    public toastr: ToastrService,
    public dialog: MatDialog
  ) {
    this.getBrowserName();
  }

  get passwordCtrl() {
    return this.loginForm.get('password') as FormControl;
  }

  get emailCtrl() {
    return this.loginForm.get('email') as FormControl;
  }
  get agreeSecurityAdvisoryCtrl() {
    return this.loginForm.get('agreeSecurityAdvisory') as FormControl;
  }


  ngOnInit() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(100)]],
      agreeSecurityAdvisory: [false, [Validators.required]]
    });
  }

  onSubmit(terminateSession: boolean = false) {
    this.loginForm.markAllAsTouched();
    this.showError = false;
    if (this.loginForm.valid && this.agreeSecurityAdvisoryCtrl.value) {
      this.auth.checkUncodedAccount(this.emailCtrl.value).subscribe(res => {
        if (res) {
          this.toastr.error(
            `Please contact your Relationship Manager for an update on access to your account. You can contact them via email help@${environment.domain} or phone ${environment.phone}`
          );
        } else {
          this.doAuthenticateRequest(terminateSession);
        }
      });
    }
  }

  doAuthenticateRequest(terminateSession: boolean = false) {
    const request = {
      email: this.emailCtrl.value,
      password: this.passwordCtrl.value,
      portalType: this.portalType,
      terminateSession: terminateSession,
    };
    this.auth.authenticate(request).subscribe(
      res => {
        this.loginInfo.currentUserStep = 'step-2';
        this.loginInfo.userId = res.userId;
        this.loginInfo.isClient = this.isClient;
        this.loginInfo.portalId = res.portalId;
        this.loginInfo.email = this.emailCtrl.value;
        this.router.navigate(['auth/login/step-2']);
      },
      err => {
        const responseData = err && err.error && err.error.responseData ? err.error.responseData : null;
        if (responseData && responseData.isUserLocked) {
          this.router.navigate(['auth/login/reactivate-account']);
        } else if (responseData && (responseData.isUserDisable === true || responseData.isUserDeleted === true)) {
          this.toastr.error(
            `Please contact your Relationship Manager for an update on access to your account. You can contact them via email help@${environment.domain} or phone ${environment.phone}`
          );
        } else if (responseData && responseData.existedSession === true) {
          this.dialog.open(CommonDialogComponent, {
            data: {
              title: err.error.messages[0],
              dialogType: 2
            }
          }).afterClosed()
            .subscribe(result => {
              if (result) {
                this.onSubmit(true);
              }
            });
        } else if (err.error.messages && err.error.messages.length > 0) {
          this.errorMessage = err.error.messages[0];
          this.showError = true;
        }
      }
    );
  }

  changeType(event: MatRadioChange) {
    this.isClient = event.value === 'client';
    this.portalType = event.value === 'client' ? PortalType.ClientPortal : PortalType.PartnerPortal;
  }

  getBrowserName() {
    const userAgent = navigator.userAgent;
    let browserName: string;
    if (userAgent.indexOf('Firefox') > -1) {
      browserName = 'Mozilla Firefox';
      // "Mozilla/5.0 (X11; Ubuntu; Linux x86_64; rv:61.0) Gecko/20100101 Firefox/61.0"
    } else if (userAgent.indexOf('SamsungBrowser') > -1) {
      browserName = 'Samsung Internet';
      // "Mozilla/5.0 (Linux; Android 9; SAMSUNG SM-G955F Build/PPR1.180610.011) AppleWebKit/537.36 (KHTML, like Gecko) SamsungBrowser/9.4 Chrome/67.0.3396.87 Mobile Safari/537.36
    } else if (userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1) {
      browserName = 'Opera';
      // "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_14_0) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/70.0.3538.102 Safari/537.36 OPR/57.0.3098.106"
    } else if (userAgent.indexOf('Trident') > -1) {
      browserName = 'Microsoft Internet Explorer';
      // "Mozilla/5.0 (Windows NT 10.0; WOW64; Trident/7.0; .NET4.0C; .NET4.0E; Zoom 3.6.0; wbx 1.0.0; rv:11.0) like Gecko"
    } else if (userAgent.indexOf('Edge') > -1) {
      browserName = 'Microsoft Edge';
      // "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.36 Edge/16.16299"
    } else if (userAgent.indexOf('Chrome') > -1) {
      browserName = this.chromeOrChromium();
      // "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Ubuntu Chromium/66.0.3359.181 Chrome/66.0.3359.181 Safari/537.36"
    } else if (userAgent.indexOf('Safari') > -1) {
      browserName = 'Apple Safari';
      // "Mozilla/5.0 (iPhone; CPU iPhone OS 11_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/11.0 Mobile/15E148 Safari/604.1 980x1306"
    } else {
      browserName = 'unknown';
    }
    this.showAlertChrome = browserName !== 'Google Chrome';
  }

  chromeOrChromium() {
    // because the lastest version Edge adopt Chromium so change user agent from Edge to Edg 
    const plugins = navigator.plugins;
    for (let i = 0; i < plugins.length; i++) {
      if (plugins[i].name && plugins[i].name.indexOf('Chromium') > -1) {
        return 'Chromium';
      } else if (plugins[i].name && plugins[i].name.indexOf('Chrome') > -1) {
        return 'Google Chrome';
      }
    }
    return 'unknown';
  }
}
